'use client';

import { contentImageAlt } from '@communio/content-image-alt';



import Link from 'next/link';

import { useEffect, useState } from 'react';

import { ClaimBadges } from '@/config/marketplaceExtensions';

import { listHaulMarketplace, type HaulListing } from '../haulApi';

import { MarketplacePageFrame } from '../MarketplacePageFrame';

import '../marketplace.css';



function formatPrice(cents: number, currency = 'USD') {

  return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);

}



function formatAgreeNet(net?: number) {

  if (net == null || net === 0) return null;

  const sign = net > 0 ? '+' : '';

  return `${sign}${Math.round(net)} agree net`;

}



const CATEGORY_OPTIONS = [

  { value: '', label: 'All categories' },

  { value: 'metal', label: 'Metal' },

  { value: 'precious_gemstone', label: 'Precious gemstone' },

  { value: 'semi_precious_gemstone', label: 'Semi-precious gemstone' },

];



export function HaulMarketplacePage() {

  const [listings, setListings] = useState<HaulListing[]>([]);

  const [nextCursor, setNextCursor] = useState<string | undefined>();

  const [loading, setLoading] = useState(true);

  const [loadingMore, setLoadingMore] = useState(false);

  const [error, setError] = useState('');

  const [formatFilter, setFormatFilter] = useState('');

  const [materialFilter, setMaterialFilter] = useState('');

  const [categoryFilter, setCategoryFilter] = useState('');

  const [search, setSearch] = useState('');

  const [searchInput, setSearchInput] = useState('');



  useEffect(() => {

    setLoading(true);

    listHaulMarketplace({

      format: formatFilter || undefined,

      material: materialFilter || undefined,

      category: categoryFilter || undefined,

      search: search || undefined,

    })

      .then((r) => {

        setListings(r.listings ?? []);

        setNextCursor(r.nextCursor);

      })

      .catch((e: Error) => setError(e.message))

      .finally(() => setLoading(false));

  }, [formatFilter, materialFilter, categoryFilter, search]);



  async function loadMore() {

    if (!nextCursor || loadingMore) return;

    setLoadingMore(true);

    try {

      const r = await listHaulMarketplace({

        cursor: nextCursor,

        format: formatFilter || undefined,

        material: materialFilter || undefined,

        category: categoryFilter || undefined,

        search: search || undefined,

      });

      setListings((prev) => [...prev, ...(r.listings ?? [])]);

      setNextCursor(r.nextCursor);

    } catch (e) {

      setError(e instanceof Error ? e.message : 'Could not load more');

    } finally {

      setLoadingMore(false);

    }

  }



  return (

    <MarketplacePageFrame

      title="Haul finds marketplace"

      description="Buy individual finds from community-approved haul reports."

    >

      <section className="feed-card settings-page marketplace-directory haul-marketplace">

        <div className="haul-marketplace__filters">

          <label>

            Search{' '}

            <input

              type="search"

              placeholder="Find name, haul, material…"

              value={searchInput}

              onChange={(e) => setSearchInput(e.target.value)}

              onKeyDown={(e) => {

                if (e.key === 'Enter') setSearch(searchInput.trim());

              }}

            />

          </label>

          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSearch(searchInput.trim())}>

            Search

          </button>

          <label>

            Format{' '}

            <select value={formatFilter} onChange={(e) => setFormatFilter(e.target.value)}>

              <option value="">All</option>

              <option value="fixed_price">Buy now</option>

              <option value="auction">Auction</option>

            </select>

          </label>

          <label>

            Category{' '}

            <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>

              {CATEGORY_OPTIONS.map((o) => (

                <option key={o.value || 'all'} value={o.value}>

                  {o.label}

                </option>

              ))}

            </select>

          </label>

          <label>

            Material{' '}

            <input

              type="text"

              placeholder="e.g. gold"

              value={materialFilter}

              onChange={(e) => setMaterialFilter(e.target.value)}

            />

          </label>

        </div>

        {loading && <p className="text-muted">Loading haul finds…</p>}

        {error && <p className="form-error">{error}</p>}

        {!loading && !error && listings.length === 0 && (

          <p className="text-muted">No community-approved find listings yet.</p>

        )}

        {listings.length > 0 && (

          <ul className="marketplace-directory__list">

            {listings.map((l) => (

              <li key={`${l.sellerUserId}-${l.id}`} className="marketplace-directory__item haul-marketplace__item">
                {l.imageUrls?.[0] && (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img src={l.imageUrls[0]} alt={contentImageAlt(l.name, 'listing photo')} className="haul-marketplace__thumb" loading="lazy" />
                )}
                <Link

                  href={`/claims/${l.sourceClaimId}/#find-${l.sourceMaterialLineId}`}

                  className="marketplace-directory__link"

                >

                  <strong>{l.name}</strong>

                  <span className="text-muted"> — {formatPrice(l.priceCents, l.currency)}</span>

                </Link>

                {l.claimTitle && <p className="text-muted">From haul: {l.claimTitle}</p>}

                {(l.communityValidityRating != null || l.agreeNet != null) && ClaimBadges && (

                  <ClaimBadges

                    communityApproved

                    communityValidity={{

                      communityValidityRating: l.communityValidityRating,

                      validityPositiveMass: l.validityPositiveMass,

                    }}

                  />

                )}

                {formatAgreeNet(l.agreeNet) && <p className="text-muted">{formatAgreeNet(l.agreeNet)}</p>}

                {l.listingFormat === 'auction' && (

                  <p className="text-muted">

                    Auction{l.currentBidCents ? ` — high bid ${formatPrice(l.currentBidCents, l.currency)}` : ''}

                    {l.auctionEndsAt ? ` — ends ${new Date(l.auctionEndsAt).toLocaleString()}` : ''}

                  </p>

                )}

              </li>

            ))}

          </ul>

        )}

        {nextCursor && (

          <p className="marketplace-directory__more">

            <button type="button" className="btn btn-ghost" disabled={loadingMore} onClick={() => void loadMore()}>

              {loadingMore ? 'Loading…' : 'Load more finds'}

            </button>

          </p>

        )}

      </section>

    </MarketplacePageFrame>

  );

}

